import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  ReactNode,
} from 'react'
import { useSelector } from 'react-redux'
import { PostHogProvider, usePostHog } from '@posthog/react'
import posthog from 'posthog-js'
import { get as getConfig } from '../../config/index'
import { isBrowser } from '../../config/environment'
import { getAppData } from '../../react-universal/browser'
import { selectAuth, selectUserCountry } from '../../services/auth/selectors'
import { selectEmail, selectUserExternalId } from '../../services/user/selectors'
import { selectUserLanguagePrimary } from '../../services/user/index'
import {
  selectGuestPhase,
  selectMembershipStatus,
  selectUserPlanSku,
  selectPlanType,
} from '../../services/user-account/selectors'
import { selectAppVersion } from '../../services/app/selectors'
import { GDPR_COUNTRIES } from '../../services/country/index'
import { getDeviceType, isTest } from '../../environment/index'
import isValidDistinctId from './isValidDistinctId'
import { POSTHOG_SESSION_RECORDING_FLAG } from './flags'
import type { DivinationChannel } from './events'

// ============================================================================
// Module-level Initialization
// ============================================================================

/**
 * Module-level PostHog initialization.
 * Runs when JavaScript is parsed, similar to the Mustache template
 * loading PostHog in the HTML head.
 */
const config = isBrowser() ? getConfig() : null
const isPostHogEnabled = !!(config?.features?.posthog)
const posthogApiKey = config?.posthogApiKey as string | undefined

// Staging runs PostHog for flag evaluation but suppresses event capture
// (and session recording) so stage usage does not pollute production analytics.
const origin = (config?.origin as string | undefined) ?? ''
const isStaging = origin.includes('www-stg-4b2c19')

// Get optional SSR bootstrap (distinctID only — feature flags evaluate client-side)
const appData = isBrowser() ? getAppData() : null
const bootstrapData = appData?.hydrate?.posthogBootstrap || {}
const bootstrappedFlags = bootstrapData.featureFlags || {}
const bootstrappedDistinctID = bootstrapData.distinctID
const bootstrappedIsIdentifiedID = bootstrapData.isIdentifiedID || false

// Session recording: fail closed at init (no SSR flag). Client enables recording
// after /flags returns when `enable-session-recording` is true (see onFeatureFlags).
const isTestEnvironment = isTest()

if (isBrowser() && isPostHogEnabled && posthogApiKey && !isTestEnvironment) {
  if (isStaging) {
    // eslint-disable-next-line no-console
    console.log('[posthog] staging: events suppressed, flags active')
  }
  posthog.init(posthogApiKey, {
    // Use managed reverse proxy to bypass ad blockers and improve event capture
    // Routes events through our own domain instead of PostHog's domain
    api_host: 'https://ph.gaia.com',
    // Keep UI links (e.g., "Open in PostHog") pointing to the correct instance
    ui_host: 'https://us.posthog.com',
    person_profiles: 'identified_only',
    capture_pageview: false,
    capture_pageleave: false,
    autocapture: false,
    capture_performance: false,
    // Client evaluates flags via /flags (SSR getAllFlags removed for TTFB).
    // Flag-gated UI uses safe defaults until hooks receive variants — promo
    // flicker after load is preferred over multi-second SSR waits.
    advanced_disable_feature_flags: false,
    // Drop every captured event on staging so we can exercise flag-gated features
    // there without polluting production analytics.
    ...(isStaging && { before_send: () => null }),
    // Fail closed: recording off until onFeatureFlags confirms eligibility.
    // Staging never records. Do not set advanced_disable_flags — that would
    // also skip feature-flag evaluation we now rely on client-side.
    disable_session_recording: true,
    // Optional distinctID from a lightweight SSR seed (no flags).
    bootstrap: {
      ...(isValidDistinctId(bootstrappedDistinctID) && {
        distinctID: bootstrappedDistinctID,
        isIdentifiedID: bootstrappedIsIdentifiedID,
      }),
      featureFlags: bootstrappedFlags as Record<string, string | boolean>,
    },
    // PostHog automatically detects ?__posthog_debug=true in URL for debug mode
    // No need to set debug in config - it's handled automatically
  })

  posthog.onFeatureFlags(() => {
    if (isStaging) {
      return
    }
    const allowed = posthog.isFeatureEnabled(POSTHOG_SESSION_RECORDING_FLAG) === true
    if (allowed) {
      posthog.startSessionRecording()
    }
  })
}

// ============================================================================
// User Identification Component
// ============================================================================

// Module-level guard: only call identify() once per page load to prevent
// redundant $set events on SPA navigation (each identify() = 1 billable event).
// Resets on full page reload, which is fine — identify is needed once per session.
let identifiedUid: string | null = null

/**
 * Handles automatic user identification based on Redux auth state.
 * Must be rendered inside the official PostHogProvider.
 */
export const PostHogUserIdentifier: React.FC<{ children: ReactNode }> = ({ children }) => {
  const posthogClient = usePostHog()
  const auth = useSelector(selectAuth)
  const uid = auth?.get('uid') as string | undefined
  const username = auth?.get('username') as string | undefined
  const country = useSelector(selectUserCountry) as string | undefined
  const email = useSelector(selectEmail) as string | undefined
  const userLanguage = useSelector(selectUserLanguagePrimary) as string | undefined

  // Engagement metrics may be absent when SSR skips PostHog person-props fetch.
  // Tenure still comes from Brooklyn user hydrate (UFT) for identify analytics.
  const hydrate = (isBrowser() ? getAppData() : null)?.hydrate
  const userInfo = hydrate?.posthogProperties?.data
  // Brooklyn tenure already in the hydrate — fall back to it so identify() still fires
  // (for analytics) even when the engagement fetch is absent.
  const fallbackTenureDays = hydrate?.user?.data?.userTenureDays
  const userTenureDays = userInfo?.userTenureDays ?? fallbackTenureDays
  const videosLast7Days = userInfo?.videosLast7Days
  const daysWithViewershipLast7Days = userInfo?.daysWithViewershipLast7Days
  const videoCompletionRate = userInfo?.videoCompletionRate
  const medianWatchTimePerWeek = userInfo?.medianWatchTimePerWeek
  const engagementCohort = userInfo?.engagementCohort
  const aiChatUsageFrequency = userInfo?.aiChatUsageFrequency
  const aiGuideInteractionCount = userInfo?.aiGuideInteractionCount

  // Conservative defaults: assume GDPR applies, assume not employee
  const isGdprCountry = country ? GDPR_COUNTRIES.includes(country) : true
  const isEmployee = email ? email.endsWith('@gaia.com') : false
  // Detect automated test accounts for filtering in PostHog
  const isTestAccount = username ? username.startsWith('automation-') : false

  // Device detection (stable for session)
  const deviceType = useMemo(() => {
    const device = getDeviceType()
    // Map to PostHog expected values (lowercase)
    if (device === 'Desktop') return 'desktop'
    if (device === 'iPhone' || device === 'Android') return 'mobile'
    if (device === 'iPad') return 'tablet'
    return 'unknown'
  }, [])

  // CRITICAL: Identify user once with all properties
  // According to PostHog docs: "You only need to call identify once per session"
  // Wait for GraphQL data before identifying to avoid multiple identify() calls
  useEffect(() => {
    // Reject falsy AND illegal sentinel ids (e.g. the string "0") — identifying
    // as one is rejected by PostHog (cannot_merge_with_illegal_distinct_id).
    if (!isValidDistinctId(uid)) {
      identifiedUid = null
      // posthog.reset() → surveys.reset() calls localStorage.removeItem without
      // guarding null localStorage (Safari private mode, blocked storage).
      try {
        posthogClient.reset()
      } catch {
        // Storage unavailable — analytics reset is best-effort on logout.
      }
      return undefined
    }

    // Wait for required properties: username and tenure (Brooklyn UFT)
    if (!username || userTenureDays === undefined) {
      return undefined
    }

    // Skip if we already identified this user this page session.
    // Prevents redundant $set events on SPA navigation remounts.
    if (identifiedUid === String(uid)) {
      return undefined
    }
    identifiedUid = String(uid)

    // Call identify() ONCE with all available properties.
    // PostHog requires uid to be a string, not a number.
    // With advanced_disable_feature_flags: false, identify() also triggers a
    // flag reload so person-property-targeted flags evaluate for this user.
    posthogClient.identify(String(uid), {
      user_id: uid, // Add user_id as property for filtering in PostHog UI
      username,
      ...(email && { email }), // Include email if available (optional)
      ...(country && { country }), // Add country if available from Redux
      ...(userLanguage && { language: userLanguage }), // Match bootstrap key for flag targeting
      is_gdpr_country: isGdprCountry,
      is_employee: isEmployee,
      is_test_account: isTestAccount,
      device_type: deviceType,
      platform: 'web',
      // GraphQL-sourced engagement properties (optional when SSR skips person props)
      user_tenure_days: userTenureDays,
      ...(videosLast7Days !== undefined && {
        videos_last_7_days: videosLast7Days,
      }),
      ...(daysWithViewershipLast7Days !== undefined && {
        days_with_viewership_last_7_days: daysWithViewershipLast7Days,
      }),
      ...(videoCompletionRate !== undefined && {
        video_completion_rate: videoCompletionRate,
      }),
      ...(medianWatchTimePerWeek !== undefined && {
        median_watch_time_per_week: medianWatchTimePerWeek,
      }),
      ...(engagementCohort !== undefined && {
        engagement_cohort: engagementCohort,
      }),
      ...(aiChatUsageFrequency !== undefined && {
        ai_chat_usage_frequency: aiChatUsageFrequency,
      }),
      ...(aiGuideInteractionCount !== undefined && {
        ai_guide_interaction_count: aiGuideInteractionCount,
      }),
    })

    return undefined
  }, [
    posthogClient,
    uid,
    username,
    email,
    country,
    userLanguage,
    isGdprCountry,
    isEmployee,
    isTestAccount,
    userTenureDays,
    deviceType,
    videosLast7Days,
    daysWithViewershipLast7Days,
    videoCompletionRate,
    medianWatchTimePerWeek,
    engagementCohort,
    aiChatUsageFrequency,
    aiGuideInteractionCount,
  ])

  return <>{children}</>
}

// ============================================================================
// Layer 1 Super-Properties Registrar
// ============================================================================

/**
 * Registers Layer 1 PostHog super-properties whenever the underlying Redux
 * values change. Super-properties are attached to every subsequent event
 * automatically by the PostHog client (see `posthog.register`). Nullish values
 * are omitted from the registered payload so PostHog does not overwrite an
 * existing super-property with `null` / `undefined`.
 *
 * Scope: the eight Layer 1 properties defined in
 * `docs/analytics/design.md` — `user_id`, `gaia_uuid`, `app_version`,
 * `user_language`, `guest_phase`, `membership_status`, `user_plan_sku`,
 * `plan_type`. Each maps to one Redux selector.
 *
 * `ai_persona` is intentionally NOT a Layer 1 super-property: it is per-
 * conversation (Layer 3), locked at chat entry, and travels with the in-chat
 * context via `ChatSessionContextProvider`.
 */
export const PostHogSuperPropertiesRegistrar: React.FC<{
  children?: ReactNode
}> = ({ children }) => {
  const posthogClient = usePostHog()
  // `selectGuestPhase` returns boolean today (true === in-trial-segment).
  // The other Layer 1 selectors return string-or-nullish. Both are passed
  // through to `posthog.register()` verbatim; the conditional spread below
  // omits any nullish/false value.
  const guestPhase = useSelector(selectGuestPhase) as unknown as boolean | string | null | undefined
  const membershipStatus = useSelector(selectMembershipStatus) as string | null | undefined
  const userLanguage = useSelector(selectUserLanguagePrimary) as string | null | undefined
  const auth = useSelector(selectAuth) as { get: (k: string) => unknown } | undefined
  const userId = auth?.get('uid') as string | null | undefined
  const gaiaUuid = useSelector(selectUserExternalId) as string | null | undefined
  const userPlanSku = useSelector(selectUserPlanSku) as string | null | undefined
  const planType = useSelector(selectPlanType) as string | null | undefined
  const appVersion = useSelector(selectAppVersion) as string | null | undefined

  useEffect(() => {
    if (!posthogClient) return
    posthogClient.register({
      ...(guestPhase && { guest_phase: guestPhase }),
      ...(membershipStatus && { membership_status: membershipStatus }),
      ...(userLanguage && { user_language: userLanguage }),
      ...(userId && { user_id: userId }),
      ...(gaiaUuid && { gaia_uuid: gaiaUuid }),
      ...(appVersion && { app_version: appVersion }),
      ...(userPlanSku && { user_plan_sku: userPlanSku }),
      ...(planType && { plan_type: planType }),
    })
  }, [
    guestPhase,
    membershipStatus,
    userLanguage,
    userId,
    gaiaUuid,
    appVersion,
    userPlanSku,
    planType,
    posthogClient,
  ])

  return <>{children}</>
}

// ============================================================================
// Main Provider
// ============================================================================

interface GaiaPostHogProviderProps {
  children: ReactNode
}

/**
 * GaiaPostHogProvider wraps the official @posthog/react PostHogProvider.
 * - Passes the pre-initialized posthog client to the official provider
 * - Handles user identification automatically via PostHogUserIdentifier
 *
 * When PostHog is disabled (not initialized), capture calls are no-ops.
 */
export const GaiaPostHogProvider: React.FC<GaiaPostHogProviderProps> = ({ children }) => {
  if (!isPostHogEnabled) {
    return <>{children}</>
  }

  return (
    <PostHogProvider client={posthog}>
      <PostHogUserIdentifier>
        <PostHogSuperPropertiesRegistrar>
          {children}
        </PostHogSuperPropertiesRegistrar>
      </PostHogUserIdentifier>
    </PostHogProvider>
  )
}

// ============================================================================
// Event Context - for context-aware event tracking
// ============================================================================

/**
 * Valid context names for PostHog events.
 * Use these to identify which feature/surface an event originated from.
 */
export type PostHogEventContext = 'ai_guide_on_the_side' | 'ai_sage_on_stage' | 'ai_search' | DivinationChannel

export const EventContext = createContext<PostHogEventContext | null>(null)

interface EventContextProviderProps {
  context: PostHogEventContext
  children: ReactNode
}

/**
 * Provides event context to child components. All events captured via usePostHogEvent() will
 * automatically include this context.
 *
 * @example
 * <EventContextProvider context="ai_guide_on_the_side">
 *   <VideoGuideChat />
 * </EventContextProvider>
 */
export const EventContextProvider: React.FC<EventContextProviderProps> = ({
  context,
  children,
}) => {
  return (
    <EventContext.Provider value={context}>
      {children}
    </EventContext.Provider>
  )
}

// ============================================================================
// Chat Session Context - in-chat context for AI conversation events
// ============================================================================

/**
 * Shape of the in-chat context exposed to descendants. All fields are
 * `string | null` so consumers can omit them safely via conditional spread
 * — `conversationTitle` arrives after the LLM generates it; `aiPersona` and
 * `chatExperienceName` are unknown outside an AI chat session.
 */
export interface ChatSessionContextValue {
  chatSessionId: string | null
  conversationTitle: string | null
  aiPersona: string | null
  chatExperienceName: string | null
  uiLocation: string | null
  screenClass: string | null
}

export const ChatSessionContext = createContext<ChatSessionContextValue | null>(null)

interface ChatSessionContextProviderProps {
  chatSessionId: string | null
  conversationTitle?: string | null
  aiPersona?: string | null
  chatExperienceName?: string | null
  uiLocation?: string | null
  screenClass?: string | null
  children: ReactNode
}

/**
 * Provides chat-session context (session id, optional conversation title,
 * locked-at-entry persona, backend chat-channel name) to descendants. Read
 * via {@link useChatSessionContext}. Mirrors the {@link EventContextProvider}
 * pattern.
 *
 * @example
 * <ChatSessionContextProvider
 *   chatSessionId={sessionId}
 *   conversationTitle={title}
 *   aiPersona="sage"
 *   chatExperienceName="Tarot"
 * >
 *   <ChatSurface />
 * </ChatSessionContextProvider>
 */
export const ChatSessionContextProvider: React.FC<ChatSessionContextProviderProps> = ({
  chatSessionId,
  conversationTitle = null,
  aiPersona = null,
  chatExperienceName = null,
  uiLocation = null,
  screenClass = null,
  children,
}) => {
  const value = useMemo<ChatSessionContextValue>(() => ({
    chatSessionId,
    conversationTitle: conversationTitle ?? null,
    aiPersona: aiPersona ?? null,
    chatExperienceName: chatExperienceName ?? null,
    uiLocation: uiLocation ?? null,
    screenClass: screenClass ?? null,
  }), [chatSessionId, conversationTitle, aiPersona, chatExperienceName, uiLocation, screenClass])

  return (
    <ChatSessionContext.Provider value={value}>
      {children}
    </ChatSessionContext.Provider>
  )
}

/**
 * Reads chat-session context. When no provider is mounted, returns an
 * all-`null` value so consumers don't have to null-check the wrapper.
 */
export const useChatSessionContext = (): ChatSessionContextValue => {
  const value = useContext(ChatSessionContext)
  if (!value) {
    return {
      chatSessionId: null,
      conversationTitle: null,
      aiPersona: null,
      chatExperienceName: null,
      uiLocation: null,
      screenClass: null,
    }
  }
  return value
}
